/**
 * A gate that answers "has this element's own box actually changed?".
 *
 * Every widget element registers a window `resize` listener that re-renders it.
 * On a desktop browser `resize` fires when the reader drags the window, which is
 * rare. On a phone it fires WHILE SCROLLING: the URL bar collapses and expands
 * and the viewport height changes with it. A `<sellwild-ad>` is a fixed
 * width/height box, so none of that is a reason to rebuild it — and rebuilding
 * calls `googletag.defineSlot` again, which registers a SECOND GAM slot for the
 * same space, re-requests it, and visibly repaints the creative. Nothing
 * destroys the first slot, so the array the refresh loop walks grows on every
 * scroll.
 *
 * Measured against the deployed sellwild bundle on a phone-sized viewport: four
 * ads on the page, one dispatched `resize` took it to eight GAM slots, four
 * events took it to twenty, eight took it to thirty-six.
 *
 * The gate keeps the last size it saw and reports a change only when the
 * element's own rounded width or height differs. A viewport-only resize is then
 * a no-op, while a real re-layout — a column that narrowed, a container that
 * went from `display: none` to visible — still re-renders as before.
 */
export interface ElementSize {
  width: number
  height: number
}

export const measureElement = (element: Element): ElementSize => {
  const rect = element.getBoundingClientRect()
  // Rounded: sub-pixel jitter from a scrollbar appearing is not a re-layout,
  // and comparing raw floats would make every such event a rebuild.
  return { width: Math.round(rect.width), height: Math.round(rect.height) }
}

/**
 * Build the gate. `measure` is injected so this stays testable without a DOM.
 *
 * The first call establishes the baseline and reports NO change: the element has
 * just been rendered by the caller, and treating its initial size as a change
 * would rebuild it immediately.
 */
export const createElementSizeGate = (measure: () => ElementSize) => {
  let last: ElementSize | null = null

  return function hasResized (): boolean {
    const next = measure()

    if (last === null) {
      last = next
      return false
    }

    if (last.width === next.width && last.height === next.height) return false

    last = next
    return true
  }
}
